import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import {
  CAD_LIST_PIN_IMAGE_ID,
  CAD_LIST_PIN_STYLE,
  CAD_LIST_PINS_LAYER_ID,
  CAD_LIST_PINS_SOURCE_ID,
} from '../../Map/styles/mapTheme';
import { whenMapStyleReady } from '../../Map/hooks/useListPinsLayer';
import { moveReferencePlacesForeground } from '../../Map/utils/referencePlaces';
import { buildCadListPinTooltipHtml } from '../utils/cadOwnerMapOverlay';
import { ensureCadListPinImageForId, ensureCadListPinImages } from '../utils/cadListPinIcon';

export { CAD_LIST_PINS_LAYER_ID, CAD_LIST_PINS_SOURCE_ID };

/**
 * Un pin por lista CAD en el mapa país. Hover = nombre + N lotes.
 * Click = abrir el detalle de esa lista (no la ficha de un lote).
 * `visible: false` oculta la capa; no la desarma. Sacar/poner un symbol
 * a mitad de un render de MapLibre tira continuePlacement (`reading 'get'`).
 */
export function useCadListPinsLayer({
  mapInstanceRef,
  pins,
  enabled,
  visible = true,
  onSelect,
}) {
  const mapRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const pinsRef = useRef(pins);
  pinsRef.current = pins;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const popupRef = useRef(null);
  const [mapAttachVersion, setMapAttachVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const map = mapInstanceRef.current;
    if (map) {
      setMapAttachVersion((version) => version + 1);
      return undefined;
    }
    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (mapInstanceRef.current) {
        setMapAttachVersion((version) => version + 1);
        window.clearInterval(intervalId);
        return;
      }
      if (attempts >= 120) window.clearInterval(intervalId);
    }, 50);
    return () => window.clearInterval(intervalId);
  }, [enabled, mapInstanceRef]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    mapRef.current = map;
    if (!map || !enabled) {
      removeCadListPinLayers(map);
      return undefined;
    }

    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 14,
      className: 'terrio-place-tooltip',
      maxWidth: '259px',
    });
    popupRef.current = popup;
    const canvas = map.getCanvas();
    let hovering = false;
    let paintQueued = false;

    const hidePopup = () => {
      popup.remove();
      if (hovering) {
        canvas.style.cursor = '';
        hovering = false;
      }
    };

    const currentGeoJSON = () => pinsToGeoJSON(pinsRef.current);

    const paint = () => {
      paintQueued = false;
      upsertCadListPinLayers(map, currentGeoJSON(), visibleRef.current, pinsRef.current);
    };

    const handleMove = (event) => {
      if (!visibleRef.current) {
        hidePopup();
        return;
      }
      const feature = event.features?.[0];
      if (!feature) {
        hidePopup();
        return;
      }
      const html = buildCadListPinTooltipHtml(feature.properties || {});
      hovering = true;
      canvas.style.cursor = 'pointer';
      if (html) popup.setLngLat(event.lngLat).setHTML(html).addTo(map);
    };

    const handleClick = (event) => {
      if (!visibleRef.current) return;
      event.preventDefault();
      if (event.originalEvent) event.originalEvent.__cadListPinClick = true;
      const props = event.features?.[0]?.properties || {};
      const slug = String(props.slug || '').trim();
      if (!slug) return;
      const lat = Number(props.lat);
      const lng = Number(props.lng);
      onSelectRef.current?.({
        id: props.id || null,
        slug,
        name: props.name || slug,
        display_mode: props.display_mode || 'map',
        can_manage: props.can_manage === true || props.can_manage === 'true',
        can_publish: props.can_publish === true || props.can_publish === 'true',
        lotCount: Number(props.lotCount) || 0,
        ...(Number.isFinite(lat) ? { lat } : {}),
        ...(Number.isFinite(lng) ? { lng } : {}),
      });
    };

    const handleMissingImage = (event) => {
      ensureCadListPinImageForId(map, event?.id);
    };

    const handleStyleData = () => {
      try {
        if (map.getSource(CAD_LIST_PINS_SOURCE_ID) && map.getLayer(CAD_LIST_PINS_LAYER_ID)) {
          ensureCadListPinImages(map, pinsRef.current);
          return;
        }
      } catch (_) { /* estilo a medias */ }
      if (paintQueued) return;
      paintQueued = true;
      window.requestAnimationFrame(paint);
    };

    const cancelWait = whenMapStyleReady(map, paint);
    map.on('styledata', handleStyleData);
    map.on('styleimagemissing', handleMissingImage);
    map.on('mouseenter', CAD_LIST_PINS_LAYER_ID, handleMove);
    map.on('mousemove', CAD_LIST_PINS_LAYER_ID, handleMove);
    map.on('mouseleave', CAD_LIST_PINS_LAYER_ID, hidePopup);
    map.on('click', CAD_LIST_PINS_LAYER_ID, handleClick);
    canvas.addEventListener('mouseleave', hidePopup);

    return () => {
      cancelWait();
      hidePopup();
      popupRef.current = null;
      try { map.off('styledata', handleStyleData); } catch (_) { /* noop */ }
      try { map.off('styleimagemissing', handleMissingImage); } catch (_) { /* noop */ }
      try { map.off('mouseenter', CAD_LIST_PINS_LAYER_ID, handleMove); } catch (_) { /* noop */ }
      try { map.off('mousemove', CAD_LIST_PINS_LAYER_ID, handleMove); } catch (_) { /* noop */ }
      try { map.off('mouseleave', CAD_LIST_PINS_LAYER_ID, hidePopup); } catch (_) { /* noop */ }
      try { map.off('click', CAD_LIST_PINS_LAYER_ID, handleClick); } catch (_) { /* noop */ }
      canvas.removeEventListener('mouseleave', hidePopup);
    };
  }, [enabled, mapInstanceRef, mapAttachVersion]);

  useEffect(() => {
    if (!enabled) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    if (!visible) popupRef.current?.remove();
    upsertCadListPinLayers(map, pinsToGeoJSON(pins), visible, pins);
  }, [enabled, pins, visible, mapInstanceRef, mapAttachVersion]);

  useEffect(() => {
    if (!enabled) removeCadListPinLayers(mapRef.current);
  }, [enabled]);

  useEffect(() => () => {
    removeCadListPinLayers(mapRef.current);
  }, []);
}

/**
 * Add/update síncrono, como en develop. Diferir add/remove con la misma
 * clave que el teardown cancelaba el pintado en el mapa país (DROP tardío
 * o source sin layer). Al abrir una lista el hook sigue armado y se oculta
 * con `visibility`.
 *
 * @param {object} map
 * @param {object} geojson
 * @param {boolean} visible
 * @param {object[]} pins
 */
function upsertCadListPinLayers(map, geojson, visible, pins) {
  try {
    if (!ensureCadListPinImages(map, pins)) return;
    const source = map.getSource(CAD_LIST_PINS_SOURCE_ID);
    const hasLayer = Boolean(map.getLayer(CAD_LIST_PINS_LAYER_ID));
    if (source && hasLayer) {
      source.setData(geojson);
      map.setLayoutProperty(
        CAD_LIST_PINS_LAYER_ID,
        'visibility',
        visible ? 'visible' : 'none',
      );
      moveReferencePlacesForeground(map);
      return;
    }
    if (source || hasLayer) removeCadListPinLayers(map);
    if (!ensureCadListPinImages(map, pins)) return;
    map.addSource(CAD_LIST_PINS_SOURCE_ID, { type: 'geojson', data: geojson });
    map.addLayer({
      id: CAD_LIST_PINS_LAYER_ID,
      type: 'symbol',
      source: CAD_LIST_PINS_SOURCE_ID,
      layout: {
        'icon-image': ['coalesce', ['get', 'icon'], CAD_LIST_PIN_IMAGE_ID],
        'icon-size': CAD_LIST_PIN_STYLE.iconSize,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
        'icon-padding': 0,
        visibility: visible ? 'visible' : 'none',
      },
    });
    moveReferencePlacesForeground(map);
  } catch (_) {
    // estilo a medias: styledata / idle reintenta
  }
}

function pinsToGeoJSON(pins) {
  const features = (Array.isArray(pins) ? pins : [])
    .filter((pin) => Number.isFinite(Number(pin?.lat)) && Number.isFinite(Number(pin?.lng)))
    .map((pin) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [Number(pin.lng), Number(pin.lat)] },
      properties: {
        id: pin.id || '',
        slug: pin.slug || '',
        name: pin.name || pin.slug || 'Lista',
        display_mode: pin.display_mode || 'map',
        can_manage: Boolean(pin.can_manage),
        can_publish: Boolean(pin.can_publish),
        lotCount: Number(pin.lotCount) || 0,
        icon: pin.icon || CAD_LIST_PIN_IMAGE_ID,
      },
    }));
  return { type: 'FeatureCollection', features };
}

/**
 * El click del pin también llega al handler genérico del mapa (parcela
 * catastral debajo). Hay que tragárselo para no abrir el detalle de lote.
 *
 * @param {object} event
 * @returns {boolean}
 */
export function isCadListPinEvent(event) {
  if (event?.originalEvent?.__cadListPinClick) return true;
  const map = event?.target;
  const point = event?.point;
  if (!map || !point || typeof map.getLayer !== 'function') return false;
  if (!map.getLayer(CAD_LIST_PINS_LAYER_ID)) return false;
  try {
    return map.queryRenderedFeatures(point, { layers: [CAD_LIST_PINS_LAYER_ID] }).length > 0;
  } catch (_) {
    return false;
  }
}

function removeCadListPinLayers(map) {
  if (!map) return;
  try {
    if (map.getLayer(CAD_LIST_PINS_LAYER_ID)) map.removeLayer(CAD_LIST_PINS_LAYER_ID);
    if (map.getSource(CAD_LIST_PINS_SOURCE_ID)) map.removeSource(CAD_LIST_PINS_SOURCE_ID);
  } catch (_) {
    // mapa o estilo aún no listos
  }
}
